import * as THREE from 'three';
import type { Terrain } from '@/terrain/Terrain';
import { TerrainRaycaster } from '@/terrain/TerrainRaycaster';

/**
 * Click-the-palette, click-the-ground placement for fauna and objects.
 *
 * The same gesture `PlacementTool` gives plants, without the half of that tool that plants do not
 * share with anything else. A plant has a brush, a maturity preview, a spacing model, a canopy
 * overlay and a plantable-surface test; a bench has none of those, and a rabbit has none of them
 * either. Reusing `PlacementTool` would have meant threading "is this a plant" through nine methods
 * of a 900-line class, and writing a third copy of raycast-and-drop would have meant fixing every
 * pointer bug twice more.
 *
 * So the shape of it is: this class owns *the gesture* — arming, the ghost, the raycast, the click,
 * Escape — and knows nothing about what is being placed. Both callers hand it two functions: how to
 * build a preview, and what to do when the ground is clicked.
 *
 * ## Why there is no validity test here
 *
 * `PlacementTool` refuses to plant on blacktop or through a wall, and neither refusal transfers.
 * Paving is where a bench *goes*; a robin on a path is right; a figure standing inside a building's
 * clearance ring is a person in a doorway. The one class that does want a footprint check is an
 * object large enough to overlap another, and that belongs with the object rather than with the
 * gesture — see `docs/PLACEABLE_CLASSES.md` § what placement is asking.
 */

export interface StaffageToolDeps {
  domElement: HTMLElement;
  getCamera: () => THREE.Camera;
  terrain: () => Terrain | null;
  /**
   * Build the hovering preview for an armed id, already offset above the ground if it belongs in the
   * air. Returning `null` arms the tool with no ghost, which is degraded but usable.
   */
  buildGhost: (id: string) => THREE.Object3D | null;
  /** Commit a placement at a world point. `single` is set when the modifier asked for exactly one. */
  onPlace: (id: string, point: THREE.Vector3, options: { single: boolean }) => void;
  /** Called whenever the tool's observable state changes, so the HUD can re-read it. */
  onChange?: () => void;
  /** A short line to show at the pointer — which item is armed, and how to place a single one. */
  onNotice?: (text: string) => void;
}

export class StaffageTool {
  private readonly raycaster = new TerrainRaycaster();
  private readonly root = new THREE.Group();
  private ghost: THREE.Object3D | null = null;
  private active = false;
  private armedId: string | null = null;

  constructor(private readonly deps: StaffageToolDeps) {
    this.root.name = 'staffage-overlay';
    this.root.visible = false;
  }

  /** The ghost preview — the caller adds this to the scene's overlay root. */
  get overlayObject(): THREE.Object3D {
    return this.root;
  }

  get isActive(): boolean {
    return this.active;
  }

  /** What is armed, or null when the tool is down. */
  get activeId(): string | null {
    return this.active ? this.armedId : null;
  }

  /** Arm the tool with an item. Re-arming with a different id swaps the ghost in place. */
  start(id: string, notice?: string): void {
    const changed = this.armedId !== id;
    this.armedId = id;
    this.active = true;
    if (changed || !this.ghost) this.rebuildGhost();
    this.attachListeners();
    if (notice) this.deps.onNotice?.(notice);
    this.deps.onChange?.();
  }

  cancel(): void {
    if (!this.active) return;
    this.active = false;
    this.root.visible = false;
    this.detachListeners();
    this.deps.onChange?.();
  }

  dispose(): void {
    this.cancel();
    this.disposeGhost();
  }

  /* ---------------------------------- Internals ---------------------------------- */

  private attachListeners(): void {
    // Re-arming while already active would otherwise stack a second copy of every listener.
    this.detachListeners();
    this.deps.domElement.addEventListener('pointermove', this.onPointerMove);
    this.deps.domElement.addEventListener('pointerdown', this.onPointerDown);
    this.deps.domElement.addEventListener('pointerleave', this.onPointerLeave);
    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('blur', this.onBlur);
  }

  private detachListeners(): void {
    this.deps.domElement.removeEventListener('pointermove', this.onPointerMove);
    this.deps.domElement.removeEventListener('pointerdown', this.onPointerDown);
    this.deps.domElement.removeEventListener('pointerleave', this.onPointerLeave);
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('blur', this.onBlur);
  }

  private rebuildGhost(): void {
    this.disposeGhost();
    if (!this.armedId) return;
    const ghost = this.deps.buildGhost(this.armedId);
    if (!ghost) return;
    applyGhostLook(ghost);
    this.ghost = ghost;
    this.root.add(ghost);
  }

  private disposeGhost(): void {
    if (!this.ghost) return;
    this.root.remove(this.ghost);
    this.ghost.traverse((node) => {
      const mesh = node as Partial<THREE.Mesh>;
      mesh.geometry?.dispose();
      const material = mesh.material;
      if (Array.isArray(material)) for (const entry of material) entry.dispose();
      else material?.dispose();
    });
    this.ghost = null;
  }

  private pointAt(e: PointerEvent): THREE.Vector3 | null {
    const terrain = this.deps.terrain();
    if (!terrain) return null;
    const rect = this.deps.domElement.getBoundingClientRect();
    const ndc = new THREE.Vector2(
      ((e.clientX - rect.left) / rect.width) * 2 - 1,
      -((e.clientY - rect.top) / rect.height) * 2 + 1,
    );
    return this.raycaster.intersect(ndc, this.deps.getCamera(), terrain.mesh);
  }

  private readonly onPointerMove = (e: PointerEvent): void => {
    if (!this.active) return;
    const point = this.pointAt(e);
    if (!point) {
      this.root.visible = false;
      return;
    }
    this.root.position.copy(point);
    this.root.visible = this.ghost !== null;
  };

  private readonly onPointerDown = (e: PointerEvent): void => {
    if (!this.active || e.button !== 0 || !this.armedId) return;
    const point = this.pointAt(e);
    if (!point) return;
    // Alt is the "just one of those" modifier. Held rather than a toggle, because placing a single
    // bird out of a flocking species is a one-off by nature — the same reasoning `PlacementTool`
    // applies to its held `Ctrl`, arrived at from the other direction.
    this.deps.onPlace(this.armedId, point, { single: e.altKey });
  };

  private readonly onPointerLeave = (): void => {
    this.root.visible = false;
  };

  private readonly onKeyDown = (e: KeyboardEvent): void => {
    if (e.key === 'Escape' && this.active) {
      e.preventDefault();
      this.cancel();
    }
  };

  private readonly onBlur = (): void => {
    this.root.visible = false;
  };
}

/**
 * Make a built view read as a preview: translucent, unshadowed, and drawn over the scene.
 *
 * `depthWrite: false` rather than `depthTest: false` — the ghost should be occluded by a building it
 * has gone behind, which is information, while still not fighting itself where its own parts
 * overlap.
 */
export function applyGhostLook(object: THREE.Object3D): void {
  object.traverse((node) => {
    const mesh = node as Partial<THREE.Mesh> & THREE.Object3D;
    if (mesh.isMesh !== true) return;
    mesh.castShadow = false;
    mesh.receiveShadow = false;
    const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    for (const material of materials) {
      if (!material) continue;
      material.transparent = true;
      material.opacity = 0.55;
      material.depthWrite = false;
    }
  });
}
